import { fmtNum, fmtTRY } from '../yeni-design/primitives';
import type { Donem } from './donem';
import type { SatisKaynaklari } from './ekranKaynaklari';
import { parametreBildiriyor, useSaSorgu } from './useSatisVerisi';
import { KapsamNotu } from './KapsamNotu';
import { DegisimRozeti, OlcuKarti } from './OlcuKarti';
import { olcuSecilebilir, type SaOlcu } from './olcu';
import { Bilgi } from './Bilgi';
import { KOMISYON_NOTU, komisyonNotuGerekirMi, sepetHesaplanirMi } from './kanal';
import { kisaTutar, oranMetni, puanMetni } from './bicim';
import {
  fisOzeti, karOzeti, kopruNotu, kopruNotuMetni, magazaSepeti, yuzdeDegisim,
  type KanalSatiri, type SaKategoriSatiri,
} from './ozetHesaplari';

interface Props {
  kaynaklar: SatisKaynaklari;
  donem: Donem;
  oncekiDonem: Donem;
  sube?: string | null;
  kanal?: string | null;
  /** Grafikte cizilen olcu: secili kart buna gore isaretlenir. */
  olcu?: SaOlcu;
  onOlcuSec?: (olcu: SaOlcu) => void;
}

/** `once` -> "Önceki ..." satiri; testid sayfa testleriyle ortak. */
function Once({ testid, deger }: { testid: string; deger: string | null }) {
  return deger ? <span data-testid={`${testid}-once`}>Önceki {deger}</span> : <span />;
}

/**
 * Satir 1 — dort kart (S256). Kaynaklar: 990040 bu + onceki (fis tabani; sube/kanal istemcide),
 * 990051 (urun tabani kar, marj, kopru). Iki taban + kopru: K2.
 */
export function OzetSerit({ kaynaklar, donem, oncekiDonem, sube = null, kanal = null, olcu = 'ciro', onOlcuSec }: Props) {
  // K10: TEK onceki pencere — 990040'in ikinci cagrisi ve 990051'in @ONCEKIBAS/@ONCEKIBIT'i ayni `oncekiDonem`.
  const f = { donem, sube, kanal, onceki: oncekiDonem };
  // 040 TEK anahtar (final-fix F-3a): sube/kanal ASLA parametre olmaz, ds bildirse bile — istemcide suzulur;
  // KanalBlok/SubeBlok/ZamanBlok/useSubeler ayni anahtari paylasir (spec §4.4).
  const fisQ = useSaSorgu<KanalSatiri>(kaynaklar.kanal040, { donem, sube: null, kanal: null });
  const fisOncekiQ = useSaSorgu<KanalSatiri>(kaynaklar.kanal040, { donem: oncekiDonem, sube: null, kanal: null });
  const kategoriQ = useSaSorgu<SaKategoriSatiri>(kaynaklar.kategori, f);
  const karKartiVar = !!kaynaklar.kategori;

  if (!kaynaklar.kanal040 && !kaynaklar.kategori) return null;

  const bu = fisQ.data ? fisOzeti(fisQ.data.data, sube, kanal) : null;
  const onceki = fisOncekiQ.data ? fisOzeti(fisOncekiQ.data.data, sube, kanal) : null;

  const sepetVar = sepetHesaplanirMi(kanal);
  const sepetBu = sepetVar && fisQ.data ? magazaSepeti(fisQ.data.data, sube) : null;
  const sepetOnceki = sepetVar && fisOncekiQ.data ? magazaSepeti(fisOncekiQ.data.data, sube) : null;
  const sepet = sepetBu ? sepetBu.sepet : null;
  const oncekiSepet = sepetOnceki ? sepetOnceki.sepet : null;

  const kar = kategoriQ.data ? karOzeti(kategoriQ.data.data) : null;
  const kopru = kategoriQ.data ? kopruNotu(kategoriQ.data.data) : null;

  const baglam = { tekGun: donem.tekGun, kanal, karVar: karKartiVar && !!kaynaklar.gunlukKar };
  const secim = (o: SaOlcu, label: string) => ({
    secili: olcu === o,
    secilebilir: olcuSecilebilir(o, baglam),
    onSec: () => onOlcuSec?.(o),
    secimEtiketi: `${label} grafiğini göster`,
  });

  return (
    // Telefonda TEK kolon (kullanici, 2026-09-15: 400 px 2x2'de milyonluk tutar tasiyordu). sm'de Net ciro tam satir,
    // kalanlar altinda; genis ekranda Net ciro 1,5 kat. Kar karti gizliyse kolonlar ona gore azalir.
    <div
      data-testid="blok-ozet"
      className={`grid grid-cols-1 gap-3 ${karKartiVar ? 'sm:grid-cols-3 lg:grid-cols-[1.5fr_1fr_1fr_1fr]' : 'sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr]'}`}
    >
      <OlcuKarti
        testid="olcu-ciro"
        label="Net ciro"
        value={bu ? fmtTRY(bu.ciro) : '—'}
        bilgi={kopru && <Bilgi testid="kopru-notu" metin={kopruNotuMetni(kopru)} />}
        vurgulu
        alt={<Once testid="olcu-ciro" deger={onceki ? kisaTutar(onceki.ciro) : null} />}
        {...secim('ciro', 'Net ciro')}
        degisim={<DegisimRozeti hazir={!!bu && !!onceki} deger={bu && onceki ? yuzdeDegisim(bu.ciro, onceki.ciro) : null} />}
      />
      <OlcuKarti
        testid="olcu-fis"
        label="Fiş"
        value={bu ? fmtNum(bu.fis) : '—'}
        // 990040 `fis_sayisi` FILTER'i (SQL 137): iade ve fiyat farki fisleri sayilmaz.
        bilgi={<Bilgi testid="bilgi-fis" metin="Satış fişleri; iade ve fiyat farkı fişleri sayılmaz." />}
        alt={<Once testid="olcu-fis" deger={onceki ? fmtNum(onceki.fis) : null} />}
        {...secim('fis', 'Fiş')}
        degisim={<DegisimRozeti hazir={!!bu && !!onceki} deger={bu && onceki ? yuzdeDegisim(bu.fis, onceki.fis) : null} />}
      />
      <OlcuKarti
        testid="olcu-sepet"
        label="Sepet ortalaması"
        value={sepet !== null ? fmtTRY(sepet) : '—'}
        bilgi={
          <Bilgi
            testid="bilgi-sepet"
            metin={
              sepetVar ? (
                'Yalnız Mağaza kanalı; kurumsal ve yemek kartı faturaları hariç.'
              ) : (
                <span data-testid="sepet-kanal-notu">Sepet ortalaması yalnız Mağaza kanalında hesaplanır.</span>
              )
            }
          />
        }
        degisim={
          sepetVar ? (
            <DegisimRozeti hazir={!!sepetBu && !!sepetOnceki} deger={sepet !== null ? yuzdeDegisim(sepet, oncekiSepet) : null} />
          ) : null
        }
        alt={<Once testid="olcu-sepet" deger={oncekiSepet !== null ? fmtTRY(oncekiSepet) : null} />}
        {...secim('sepet', 'Sepet ortalaması')}
      />
      {/* Spec §5: kaynagi olmayan blok gizlenir — kar karti bos "—" ile yer tutmaz. */}
      {karKartiVar && (
        <OlcuKarti
          testid="olcu-kar"
          label="Brüt kâr"
          value={kar ? fmtTRY(kar.kar) : '—'}
          bilgi={
            <Bilgi
              testid="bilgi-kar"
              metin={
                <>
                  Stok kartındaki son alış fiyatıyla.
                  {/* G1'den tasinan zorunlu not: komisyon ERP'de yok. */}
                  {komisyonNotuGerekirMi(kanal) && (
                    <>
                      {' '}
                      <span data-testid="komisyon-notu">{KOMISYON_NOTU}.</span>
                    </>
                  )}
                  <KapsamNotu sube={sube} destekli={parametreBildiriyor(kaynaklar.kategori, '@LOKASYON')} />
                </>
              }
            />
          }
          degisim={<DegisimRozeti hazir={kategoriQ.data !== undefined} deger={kar ? yuzdeDegisim(kar.kar, kar.oncekiKar) : null} />}
          alt={<Once testid="olcu-kar" deger={kar && kar.oncekiKar !== null ? kisaTutar(kar.oncekiKar) : null} />}
          {...secim('kar', 'Brüt kâr')}
          ek={
            kar && kar.marj !== null ? (
              <span data-testid="olcu-marj">
                Marj {oranMetni(kar.marj)}
                {kar.oncekiMarj !== null && <> · {puanMetni(kar.marj - kar.oncekiMarj)}</>}
              </span>
            ) : null
          }
        />
      )}
    </div>
  );
}
